import { useState, useCallback } from "react";
import React from "react";
import GitHubOrgWarningModal from "../components/integrations/GitHubOrgWarningModal";

import PostHogInfoModal from "../components/integrations/PostHogInfoModal";
import AmplitudeInfoModal from "../components/integrations/AmplitudeInfoModal";
import MixpanelInfoModal from "../components/integrations/MixpanelInfoModal";
import HeapInfoModal from "../components/integrations/HeapInfoModal";
import SegmentInfoModal from "../components/integrations/SegmentInfoModal";
import { API_KEY_IDS } from "../config/integrations";

/**
 * Shared hook for pre-connection modal logic.
 *
 * Replaces the duplicated 12+ state variables and if-blocks in both
 * Integrations.tsx and SelectIntegrations.tsx.
 *
 * Usage:
 *   const { preConnectCheck, renderModals } = useIntegrationModals(onConnect);
 *   // In your handleConnect / handleTileClick:
 *   if (!preConnectCheck(providerId)) return; // modal shown, stop
 *   // ...proceed with connection
 */
export function useIntegrationModals(
  onConnect: (providerId: string) => void,
) {
  const [activeModal, setActiveModal] = useState<string | null>(null);

  /**
   * Call before connecting. Returns `true` if no modal is needed (proceed
   * immediately). Returns `false` if a pre-connection modal was shown
   * (caller should stop and wait for the modal callback).
   */
  const preConnectCheck = useCallback((providerId: string): boolean => {
    if (providerId === "github") {
      setActiveModal("github");
      return false;
    }
    if (API_KEY_IDS.has(providerId)) {
      setActiveModal(providerId);
      return false;
    }
    return true;
  }, []);

  const dismiss = useCallback(() => setActiveModal(null), []);

  const proceed = useCallback(
    (id: string) => {
      setActiveModal(null);
      onConnect(id);
    },
    [onConnect],
  );

  /** Render all pre-connection modals. Include this in your JSX. */
  const renderModals = useCallback(
    () => {
      return React.createElement(
        React.Fragment,
        null,
        React.createElement(GitHubOrgWarningModal, {
          isOpen: activeModal === "github",
          onCancel: dismiss,
          onProceed: () => proceed("github"),
        }),
        React.createElement(PostHogInfoModal, {
          isOpen: activeModal === "posthog",
          onClose: dismiss,
          onContinue: () => proceed("posthog"),
        }),
        React.createElement(AmplitudeInfoModal, {
          isOpen: activeModal === "amplitude",
          onClose: dismiss,
          onContinue: () => proceed("amplitude"),
        }),
        React.createElement(MixpanelInfoModal, {
          isOpen: activeModal === "mixpanel",
          onClose: dismiss,
          onContinue: () => proceed("mixpanel"),
        }),
        React.createElement(HeapInfoModal, {
          isOpen: activeModal === "heap",
          onClose: dismiss,
          onContinue: () => proceed("heap"),
        }),
        React.createElement(SegmentInfoModal, {
          isOpen: activeModal === "segment",
          onClose: dismiss,
          onContinue: () => proceed("segment"),
        }),
      );
    },
    [activeModal, dismiss, proceed],
  );

  return { preConnectCheck, renderModals, activeModal };
}
